import React, { 
    useState, 
    useEffect,
    useRef } from 'react';
import { BrowserRouter } from 'react-router-dom';
import Cookies from 'js-cookie';
import { QueryClient, useMutation } from '@tanstack/react-query';

import { NotificationContext } from './context/NotificationContext';
import { getBasketData, refetchBasketData } from './queries/global';

import * as UTILS from './utils/utils';
import * as QUERIES from "./queries/query-functions";

import Router from './router';

const queryClient = new QueryClient();

const App = () => {

    const [notification, setNotification] = useState(null);

    const { data: basketData } = getBasketData();

    const queryParams = new URLSearchParams(window.location.search);
    const promoCodeRef = useRef(queryParams.get('promo'));
    const membershipRef = useRef({ membership : queryParams.get('membership_id'), autoRenew : queryParams.get("autorenew") });
    const donationRef = useRef({ FundId : queryParams.get("fund_id"), Amount : queryParams.get('amount') });

    const patchBasket = useMutation({
        mutationFn: () => QUERIES.patchBasket({ PromoCode : promoCodeRef.current }),
        onSuccess: (data) => {
            setNotification({
                type: 'success',
                message : `Your promotional code "${data.promoCode}" has been successfully applied and will be added at checkout.`
            });
            refetchBasketData();
        },
    });

    const addMembership = useMutation({
        mutationFn: () => QUERIES.addMembership([ membershipRef.current ]),
        onSuccess: () => {
            setNotification({
                type: 'success',
                message : `Your membership has been successfully added to the basket.`
            });
            refetchBasketData();
        },
    });

    const addDonation = useMutation({
        mutationFn: () => QUERIES.addDonation([ donationRef.current ]),
        onSuccess: () => {     
            setNotification({
                type: 'success',
                message : `Your donation has been successfully added to the basket.`
            }); 
            refetchBasketData();
        },
    });

    useEffect(() => {
        if (promoCodeRef?.current) {
            // If there is a promo code in the URL add it to the basket on load
            patchBasket.mutateAsync();
        }
        if (membershipRef?.current?.membership) {
            // If there is a membership id in the URL add it to the basket on load
            addMembership.mutateAsync();
        }
        if (donationRef?.current?.FundId && donationRef.current.Amount) {
            // If there is a donation fund in the URL add it to the basket on load
            addDonation.mutateAsync();
        }
    }, []);

    useEffect(() => {
        if (!basketData) return;

        // Dispatch a basketCount event so that elements outside of the pathway can be updated
        const ticket_count = basketData.tickets ? basketData.tickets.length : 0;
        const membership_count = basketData.membershipSubscriptions ? basketData.membershipSubscriptions.length : 0;

        let eligible_donations = null;
        if (membership_count && basketData.donations) {
            // If there are memberships in the basket filter out any associated donations so they show as 1 item
            const excluded_donations = UTILS.getExcludedDonations(basketData);
            eligible_donations = basketData.donations.filter((donation) => !excluded_donations.includes(donation.id));
        } else {
            eligible_donations = basketData.donations;
        }
        const donation_count = eligible_donations ? eligible_donations.length : 0;

        const basket_count = ticket_count + membership_count + donation_count;

        Cookies.set("basket_count", basket_count);
        document.dispatchEvent(
            new CustomEvent("basketCount", {
                detail: { count: basket_count },
                bubbles: true,
                composed: true,
            }),
        );

    }, [basketData]);

    return (
        <div className="booking-pathway">
            <BrowserRouter>
                <NotificationContext.Provider value={{ notification, setNotification }}>
                    <Router />
                </NotificationContext.Provider>
            </BrowserRouter>
        </div>
    );
};

export default App;
export { queryClient };